import { HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Observable, catchError, map, of, switchMap } from 'rxjs';
import { ApiService } from '../core/services/api.service';
import { HttpService } from '../core/services/http.service';
import { etagFromResponse, formatEtagForQuery } from '../core/utils/etag.util';

export interface AvatarUploadResponse {
    success: boolean;
    avatarUrl?: string;
    message?: string;
}

import { User } from '../core/models/user.model';

@Injectable({
    providedIn: 'root',
})
export class AvatarService {
    private readonly httpService = inject(HttpService);
    private readonly sanitizer = inject(DomSanitizer);
    private readonly apiService = inject(ApiService);

    /**
     * Fetch user avatar with ETag for optimistic concurrency control
     */
    public fetchB2CAvatarWithETag(): Observable<{ blob: Blob; etag: string | null }> {
        return this.httpService.getBlobWithHeaders$(this.apiService.pathUrl.userAvatar()).pipe(
            map((response: HttpResponse<Blob>) => ({
                blob: response.body!,
                etag: etagFromResponse(response),
            })),
            catchError(error => {
                throw error;
            })
        );
    }

    /**
     * Update user avatar in the current user object
     */
    public updateUserAvatar(currentUser: User | null, avatarUrl: SafeResourceUrl | null, updateUserCallback: (user: User) => void): void {
        if (currentUser) {
            const updatedUser = { ...currentUser, avatarUrl };
            updateUserCallback(updatedUser);
        }
    }

    /**
     * Load user avatar - fetches avatar with ETag and updates user object
     */
    public loadUserAvatar(currentUser: User | null, updateUserCallback: (user: User) => void): void {
        this.fetchB2CAvatarWithETag().subscribe({
            next: ({ blob }) => {
                if (!blob || !(blob instanceof Blob)) {
                    return;
                }

                try {
                    const url = URL.createObjectURL(blob);
                    const safeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
                    this.updateUserAvatar(currentUser, safeUrl, updateUserCallback);
                } catch (error) {
                    console.warn('Failed to create object URL for avatar:', error);
                }
            },
            error: error => {
                // 404 means no avatar exists yet, which is fine
                if (error.status !== 404) {
                    console.warn('Failed to fetch user avatar:', error);
                }
            },
        });
    }

    /**
     * Upload user avatar to Azure B2C
     */
    public uploadAvatar(file: File): Observable<AvatarUploadResponse> {
        return this.uploadAvatarWithVersion(file);
    }

    /**
     * Upload user avatar to Azure B2C with version (ETag) for optimistic concurrency control
     */
    public uploadAvatarWithVersion(file: File, version?: string): Observable<AvatarUploadResponse> {
        const formData = new FormData();
        formData.append('image', file);

        const versionValue = formatEtagForQuery(version);
        const params = versionValue ? this.apiService.params({ version: versionValue }) : new HttpParams();
        const paramsString = params.toString();
        const url = paramsString ? `${this.apiService.pathUrl.uploadAvatar()}?${paramsString}` : this.apiService.pathUrl.uploadAvatar();

        return this.httpService.putWithResponse$(url, formData).pipe(
            switchMap(resp => {
                const ok = resp.status >= 200 && resp.status < 300;
                if (!ok) {
                    return of({ success: false, message: `Upload failed (status ${resp.status})` });
                }
                return of({ success: true } as AvatarUploadResponse);
            }),
            catchError(error => {
                console.error('Failed to upload avatar:', error);
                return of({
                    success: false,
                    message: 'Failed to upload avatar. Please try again.',
                });
            })
        );
    }

    /**
     * Get avatar URL from blob
     */
    public getAvatarUrl(blob: Blob): SafeResourceUrl {
        const url = URL.createObjectURL(blob);
        return this.sanitizer.bypassSecurityTrustResourceUrl(url);
    }

    /**
     * Validate avatar file
     */
    public validateAvatarFile(file: File): { valid: boolean; message?: string } {
        const maxSize = 5 * 1024 * 1024; // 5MB
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];

        if (!file) {
            return { valid: false, message: 'No file selected' };
        }

        if (!allowedTypes.includes(file.type)) {
            return {
                valid: false,
                message: 'Please select a valid image file (JPEG, PNG, GIF, or WebP).',
            };
        }

        if (file.size > maxSize) {
            return {
                valid: false,
                message: 'File size must be less than 5MB.',
            };
        }

        return { valid: true };
    }

    /**
     * Delete user avatar
     */
    public deleteAvatar(): Observable<void> {
        return this.httpService.delete$<void>(this.apiService.pathUrl.userAvatar());
    }

    /**
     * Create a preview of the avatar before upload
     */
    public createAvatarPreview(file: File): Observable<SafeResourceUrl> {
        return new Observable(observer => {
            const reader = new FileReader();
            reader.onload = (e: ProgressEvent<FileReader>) => {
                const result = e.target?.result as string;
                const safeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(result);
                observer.next(safeUrl);
                observer.complete();
            };
            reader.onerror = () => {
                observer.error('Failed to read file');
            };
            reader.readAsDataURL(file);
        });
    }

    //------------------------------------ contact person -------------------------------------------
    /**
     * generic fetch function for avatar
     */
    public fetchAvatarWithETag(url: string): Observable<{ blob: Blob; etag: string | null }> {
        return this.httpService.getBlobWithHeaders$(url).pipe(
            map((response: HttpResponse<Blob>) => ({
                blob: response.body!,
                etag: etagFromResponse(response),
            })),
            catchError(error => {
                throw error;
            })
        );
    }

    public fetchUserAvatarWithETag() {
        return this.fetchAvatarWithETag(this.apiService.pathUrl.userAvatar());
    }

    public fetchContactAvatarWithETag(customerId: string, contactId: string) {
        return this.fetchAvatarWithETag(this.apiService.pathUrl.customerContactAvatar(customerId, contactId));
    }

    uploadAvatarToUrl(url: string, file: File, version?: string): Observable<AvatarUploadResponse> {
        const formData = new FormData();
        formData.append('file', file);

        const versionValue = formatEtagForQuery(version);
        const params = versionValue ? this.apiService.params({ version: versionValue }) : new HttpParams();
        const paramsString = params.toString();
        const finalUrl = paramsString ? `${url}?${paramsString}` : url;

        return this.httpService.putWithResponse$(finalUrl, formData).pipe(
            switchMap(resp => of({ success: resp.status >= 200 && resp.status < 300 })),
            catchError(() => of({ success: false, message: 'Failed to upload avatar. Please try again.' }))
        );
    }

    uploadContactAvatar(customerId: string, contactId: string, file: File, version?: string): Observable<AvatarUploadResponse> {
        return this.uploadAvatarToUrl(this.apiService.pathUrl.customerContactAvatar(customerId, contactId), file, version);
    }

    public deleteContactAvatar(customerId: string, contactId: string): Observable<void> {
        return this.httpService.delete$<void>(this.apiService.pathUrl.customerContactAvatar(customerId, contactId));
    }
}
